import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useStore } from '../state/store';
import { showNotice } from '../state/actions';
import { useTheme } from '../theme/useTheme';

export function Page({
  children,
  title,
  onBack,
  compact = false,
}: {
  children: ReactNode;
  title?: string;
  onBack?: () => void;
  compact?: boolean;
}) {
  const th = useTheme();
  return (
    <main className={`page ${compact ? 'page-compact' : ''}`}>
      <header className="masthead">
        {onBack ? (
          <button type="button" className="back" onClick={onBack} aria-label="Back">
            ‹ Back
          </button>
        ) : null}
        <div className="wordmark">{th.t.title}</div>
        {title ? <h1>{title}</h1> : null}
      </header>
      <NoticeBar />
      {children}
    </main>
  );
}

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'quiet' | 'danger' }) {
  return <button type="button" className={`btn btn-${variant} ${className}`} {...props} />;
}

export function Field({ label, hint, ...input }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input className="field-input" {...input} />
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Section({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <section className="section">
      {label ? <h2 className="section-label">{label}</h2> : null}
      {children}
    </section>
  );
}

export function Narrator({ children }: { children: ReactNode }) {
  return <p className="narrator">{children}</p>;
}

function NoticeBar() {
  const notice = useStore((s) => s.notice);
  if (!notice) return null;
  return (
    <div className={`notice notice-${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>
      <span>{notice.text}</span>
      <button type="button" className="notice-close" aria-label="Dismiss" onClick={() => showNotice(null)}>
        ×
      </button>
    </div>
  );
}
